import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  ChartBarIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface PlatformStats {
  totalGarages: number;
  activeGarages: number;
  totalUsers: number;
  totalRevenue: number;
  monthlyRevenue: number;
  subscriptionRenewalRate: number;
  averageRevenuePerGarage: number;
  topPerformingGarages: GaragePerformance[];
}

interface GaragePerformance {
  id: number;
  name: string;
  monthlyRevenue: number;
  customerCount: number;
  subscriptionStatus: string;
  lastActive: string;
}

interface SubscriptionMetrics {
  activeSubscriptions: number;
  expiringThisMonth: number;
  expiredSubscriptions: number;
  trialSubscriptions: number;
  subscriptionsByPlan: Record<string, number>;
}

interface RevenueData {
  labels: string[];
  platformRevenue: number[];
  garageRevenue: number[];
}

const SuperAdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [subscriptionMetrics, setSubscriptionMetrics] = useState<SubscriptionMetrics | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showGarageModal, setShowGarageModal] = useState(false);
  const [selectedGarage, setSelectedGarage] = useState<GaragePerformance | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, metricsRes, revenueRes] = await Promise.all([
        apiClient.get(`/admin/platform-stats?period=${selectedPeriod}`),
        apiClient.get('/admin/subscription-metrics'),
        apiClient.get(`/admin/revenue-data?period=${selectedPeriod}`),
      ]);

      setPlatformStats(statsRes.data.data);
      setSubscriptionMetrics(metricsRes.data.data);
      setRevenueData(revenueRes.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set fallback data when API fails or is not implemented yet
      setPlatformStats({
        totalGarages: 125,
        activeGarages: 118,
        totalUsers: 1547,
        totalRevenue: 45750,
        monthlyRevenue: 12500,
        subscriptionRenewalRate: 94,
        averageRevenuePerGarage: 366,
        topPerformingGarages: [
          {
            id: 1,
            name: 'كراج السلام للسيارات',
            monthlyRevenue: 2500,
            customerCount: 156,
            subscriptionStatus: 'active',
            lastActive: '2024-01-15'
          },
          {
            id: 2,
            name: 'ورشة الخليج الحديثة',
            monthlyRevenue: 2200,
            customerCount: 134,
            subscriptionStatus: 'active',
            lastActive: '2024-01-15'
          }
        ]
      });

      setSubscriptionMetrics({
        activeSubscriptions: 118,
        expiringThisMonth: 12,
        expiredSubscriptions: 7,
        trialSubscriptions: 8,
        subscriptionsByPlan: {
          'Basic': 45,
          'Professional': 58,
          'Enterprise': 15
        }
      });

      setRevenueData({
        labels: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو'],
        platformRevenue: [8500, 9200, 10100, 11000, 11800, 12500],
        garageRevenue: [25000, 27500, 30200, 32800, 35100, 38000]
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    trend, 
    color = 'primary' 
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    trend?: { value: number; isPositive: boolean };
    color?: string;
  }) => {
    const colorClasses = {
      primary: 'bg-[#008B8B]/10 text-[#008B8B]',
      secondary: 'bg-[#FF8C00]/10 text-[#FF8C00]', 
      success: 'bg-[#10B981]/10 text-[#10B981]',
      warning: 'bg-[#F59E0B]/10 text-[#F59E0B]',
      danger: 'bg-[#EF4444]/10 text-[#EF4444]',
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#008B8B] hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
            {trend && (
              <div className="mt-2 flex items-center">
                {trend.isPositive ? (
                  <ArrowUpIcon className="w-4 h-4 text-[#10B981] mr-1" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4 text-[#EF4444] mr-1" />
                )}
                <span
                  className={`text-sm font-medium ${
                    trend.isPositive ? 'text-[#10B981]' : 'text-[#EF4444]'
                  }`}
                >
                  {Math.abs(trend.value)}%
                </span>
                <span className="text-sm text-gray-500 ml-1">vs last period</span>
              </div>
            )}
          </div>
          <div className={`ml-4 p-4 rounded-xl ${colorClasses[color as keyof typeof colorClasses]}`}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  const renderRevenueChart = () => {
    if (!revenueData) return null;

    const data = {
      labels: revenueData.labels,
      datasets: [
        {
          label: 'Platform Revenue (Subscriptions)',
          data: revenueData.platformRevenue,
          borderColor: '#008B8B',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Garage Revenue (Payments)',
          data: revenueData.garageRevenue,
          borderColor: '#FF8C00',
          backgroundColor: 'rgba(255, 140, 0, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: windowWidth < 768 ? 'bottom' as const : 'top' as const,
          labels: {
            usePointStyle: true,
            padding: windowWidth < 768 ? 10 : 20,
            font: {
              size: windowWidth < 768 ? 10 : 12,
            },
          },
        },
        title: {
          display: true,
          text: 'Revenue Overview',
          font: {
            size: windowWidth < 768 ? 14 : 16,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: windowWidth < 768 ? 10 : 12,
            },
            maxRotation: windowWidth < 768 ? 45 : 0,
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: {
              size: windowWidth < 768 ? 10 : 12,
            },
            callback: (value: any) => {
              // Shorter format for mobile
              if (windowWidth < 768) {
                return `${(value / 1000).toFixed(0)}K`;
              }
              return formatCurrency(value);
            },
          },
        },
      },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
    };

    return <Line data={data} options={options} />;
  };

  const renderSubscriptionChart = () => {
    if (!subscriptionMetrics) return null;

    const data = {
      labels: Object.keys(subscriptionMetrics.subscriptionsByPlan),
      datasets: [
        {
          data: Object.values(subscriptionMetrics.subscriptionsByPlan),
          backgroundColor: [
            '#008B8B',
            '#FF8C00', 
            '#F59E0B',
            '#10B981',
          ],
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: windowWidth < 768 ? 'bottom' as const : 'right' as const,
          labels: {
            usePointStyle: true,
            padding: windowWidth < 768 ? 10 : 20,
            font: {
              size: windowWidth < 768 ? 10 : 12,
            },
          },
        },
        title: {
          display: true,
          text: 'Subscriptions by Plan',
          font: {
            size: windowWidth < 768 ? 14 : 16,
          },
        },
        tooltip: {
          callbacks: {
            label: function(context: any) {
              const label = context.label || '';
              const value = context.parsed;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} (${percentage}%)`;
            },
          },
        },
      },
    };

    return <Doughnut data={data} options={options} />;
  };

  const handleGarageClick = (garage: GaragePerformance) => {
    setSelectedGarage(garage);
    setShowGarageModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="super_admin"
        showNotifications={true}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Dashboard Title and Welcome */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {t('superAdminDashboard')}
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                {t('platformOverview')} - {t('welcome')}, {user?.name || 'Admin'}
              </p>
            </div>
            <div className="mt-4 lg:mt-0">
              <div className="bg-gradient-to-r from-[#008B8B] to-[#006666] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg">
                <div className="text-xs sm:text-sm text-white/80">{t('totalRevenue')}</div>
                <div className="text-lg sm:text-xl font-bold text-white">
                  {formatCurrency(platformStats?.totalRevenue || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Period Selector */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:justify-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:hidden">Time Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full sm:w-auto bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 py-2 rounded-lg text-sm"
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
          </div>
        </div>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <StatCard
            title={t('totalGarages')}
            value={platformStats?.totalGarages || 0}
            icon={<BuildingStorefrontIcon className="w-8 h-8" />}
            trend={{ value: 15, isPositive: true }}
            color="primary"
          />
          <StatCard
            title={t('activeUsers')}
            value={platformStats?.totalUsers || 0}
            icon={<UsersIcon className="w-8 h-8" />}
            trend={{ value: 8, isPositive: true }}
            color="success"
          />
          <StatCard
            title={t('monthlyGrowth')}
            value={formatCurrency(platformStats?.monthlyRevenue || 0)}
            icon={<CurrencyDollarIcon className="w-8 h-8" />}
            trend={{ value: 12, isPositive: true }}
            color="secondary"
          />
          <StatCard
            title="Renewal Rate"
            value={`${platformStats?.subscriptionRenewalRate || 0}%`}
            icon={<ChartBarIcon className="w-8 h-8" />}
            trend={{ value: 3, isPositive: false }}
            color="warning"
          />
        </div>

        {/* Subscription Alerts */}
        {subscriptionMetrics && subscriptionMetrics.expiringThisMonth > 0 && (
          <div className="mb-6 sm:mb-8 bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-yellow-800">
                  <span className="font-semibold">{subscriptionMetrics.expiringThisMonth}</span> subscriptions 
                  expiring this month. Consider sending renewal reminders.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-[#008B8B]">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <ChartBarIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#008B8B] mr-2" />
              <span className="truncate">{t('revenueChart')}</span>
            </h2>
            <div className="h-48 sm:h-64 lg:h-72">
              {renderRevenueChart()}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-[#FF8C00]">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <UsersIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#FF8C00] mr-2" />
              <span className="truncate">Subscription Distribution</span>
            </h2>
            <div className="h-48 sm:h-64 lg:h-72">
              {renderSubscriptionChart()}
            </div>
          </div>
        </div>

        {/* Top Performing Garages */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-l-4 border-[#F59E0B]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <BuildingStorefrontIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#F59E0B] mr-2" />
              <span className="truncate">{t('manageGarages')}</span>
            </h2>
            <button className="text-xs sm:text-sm text-[#008B8B] hover:text-[#006666] font-medium">
              {t('viewAll')}
            </button>
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full py-2 align-middle">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Garage
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Revenue
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Customers
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Last Active
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {platformStats?.topPerformingGarages.map((garage) => (
                  <tr key={garage.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-32 sm:max-w-none">{garage.name}</div>
                      <div className="sm:hidden text-xs text-gray-500 mt-1">
                        {formatCurrency(garage.monthlyRevenue)} • {garage.customerCount} customers
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-xs sm:text-sm text-gray-900">{formatCurrency(garage.monthlyRevenue)}</div>
                    </td>
                    <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-xs sm:text-sm text-gray-900">{garage.customerCount}</div>
                    </td>
                    <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          garage.subscriptionStatus === 'active'
                            ? 'bg-green-100 text-green-800'
                            : garage.subscriptionStatus === 'trial'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {garage.subscriptionStatus}
                      </span>
                      <div className="lg:hidden text-xs text-gray-500 mt-1">
                        {new Date(garage.lastActive).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden lg:table-cell">
                      {new Date(garage.lastActive).toLocaleDateString()}
                    </td>
                    <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      <button
                        onClick={() => handleGarageClick(garage)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Commission Management Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-l-4 border-[#10B981]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <CurrencyDollarIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#10B981] mr-2" />
              <span className="truncate">Commission Management</span>
            </h2>
            <button className="text-xs sm:text-sm bg-[#10B981] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#059669] w-full sm:w-auto">
              Generate Payout File
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">Active Referrals</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">23</p>
              <p className="text-xs text-gray-500 mt-1">+5 this month</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">Monthly Commissions</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(230)}</p>
              <p className="text-xs text-gray-500 mt-1">10 KWD per referral</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 sm:col-span-2 lg:col-span-1">
              <p className="text-xs sm:text-sm text-gray-600">Total Paid Out</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(2760)}</p>
              <p className="text-xs text-gray-500 mt-1">Since inception</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Commission Rate:</span>
              <span className="font-medium">10 KWD/month per referral</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Commission Duration:</span>
              <span className="font-medium">36 months</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Next Payout Date:</span>
              <span className="font-medium">End of month</span>
            </div>
          </div>
        </div>

        {/* Partnership Revenue Sharing */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-l-4 border-[#8B008B]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <UsersIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#8B008B] mr-2" />
              <span className="truncate">Partnership Revenue Sharing</span>
            </h2>
            <button 
              onClick={() => navigate('/dashboard/revenue-sharing')}
              className="text-xs sm:text-sm bg-[#8B008B] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#6B006B] w-full sm:w-auto">
              Manage Revenue Sharing
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3">Current Month Summary</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Total Platform Revenue:</span>
                  <span className="text-xs sm:text-sm font-medium">{formatCurrency(12500)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Partnership Split (50%):</span>
                  <span className="text-xs sm:text-sm font-medium text-[#8B008B]">{formatCurrency(6250)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Your Share (50%):</span>
                  <span className="text-xs sm:text-sm font-medium text-[#008B8B]">{formatCurrency(6250)}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3">Partnership Configuration</h3>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">Revenue Split:</span>
                  <div className="flex items-center gap-2">
                    <input 
                      type="number" 
                      value="50" 
                      className="w-16 px-2 py-1 border rounded text-xs sm:text-sm"
                      min="0"
                      max="100"
                    />
                    <span className="text-xs sm:text-sm">%</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">Payment Frequency:</span>
                  <select className="text-xs sm:text-sm border rounded px-2 py-1 w-full sm:w-auto">
                    <option>Monthly</option>
                    <option>Quarterly</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Subscription Management */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-l-4 border-[#EF4444]">
          <div className="flex flex-col gap-3 sm:gap-0 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <CreditCardIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#EF4444] mr-2" />
              <span className="truncate">Subscription Management</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <button className="text-xs sm:text-sm bg-[#25D366] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#128C7E]">
                Send WhatsApp Reminders
              </button>
              <button className="text-xs sm:text-sm bg-[#EF4444] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#DC2626]">
                Bulk Renewal
              </button>
            </div>
          </div>
          
          {/* Expiring Subscriptions Alert */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  {subscriptionMetrics?.expiringThisMonth || 12} subscriptions expiring this month
                </p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-red-700">• كراج السلام - Expires in 3 days</p>
                  <p className="text-xs text-red-700">• ورشة الخليج - Expires in 5 days</p>
                  <p className="text-xs text-red-700">• مركز الصيانة المتقدم - Expires in 7 days</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">Trial → Paid</p>
              <p className="text-xl sm:text-2xl font-bold text-[#10B981]">75%</p>
              <p className="text-xs text-gray-500 mt-1">Conversion rate</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">Active Trials</p>
              <p className="text-xl sm:text-2xl font-bold text-[#F59E0B]">{subscriptionMetrics?.trialSubscriptions || 8}</p>
              <p className="text-xs text-gray-500 mt-1">14-day trials</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">Renewal Rate</p>
              <p className="text-xl sm:text-2xl font-bold text-[#008B8B]">94%</p>
              <p className="text-xs text-gray-500 mt-1">Last 3 months</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">Churned</p>
              <p className="text-xl sm:text-2xl font-bold text-[#EF4444]">{subscriptionMetrics?.expiredSubscriptions || 7}</p>
              <p className="text-xs text-gray-500 mt-1">This month</p>
            </div>
          </div>
        </div>

        {/* Financial Dashboard */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-l-4 border-[#FFD700]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <ChartBarIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#FFD700] mr-2" />
              <span className="truncate">Financial Dashboard</span>
            </h2>
            <button className="text-xs sm:text-sm bg-[#FFD700] text-gray-900 px-3 sm:px-4 py-2 rounded-lg hover:bg-[#FFC700] w-full sm:w-auto">
              Export Financial Report
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Revenue Breakdown</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Platform Subscriptions</span>
                    <span className="text-sm font-medium">{formatCurrency(12500)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#008B8B] h-2 rounded-full" style={{width: '75%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Payment Processing Fees</span>
                    <span className="text-sm font-medium">{formatCurrency(3800)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#FF8C00] h-2 rounded-full" style={{width: '25%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Payout Summary</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Commission Payouts</span>
                  <span className="text-sm font-medium text-red-600">-{formatCurrency(230)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Partner Revenue Share</span>
                  <span className="text-sm font-medium text-red-600">-{formatCurrency(6250)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium text-gray-900">Net Revenue</span>
                  <span className="text-lg font-bold text-[#10B981]">{formatCurrency(9820)}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-900">Total Profit</span>
                  <span className="text-xl font-bold text-[#10B981]">
                    {formatCurrency((platformStats?.totalRevenue || 16300) - 3800 - 230 - 6250)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Revenue ({formatCurrency(platformStats?.totalRevenue || 16300)}) - Processing Fees ({formatCurrency(3800)}) - Commissions ({formatCurrency(230)}) - Partner Share ({formatCurrency(6250)})
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* System Configuration Panel */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-l-4 border-[#4B5563]">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
            <CogIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#4B5563] mr-2" />
            <span className="truncate">System Configuration</span>
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Referral Settings</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">Commission Amount</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="number" value="10" className="w-20 px-3 py-2 border rounded-lg text-sm" />
                    <span className="text-sm">KWD/month</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Duration</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="number" value="36" className="w-20 px-3 py-2 border rounded-lg text-sm" />
                    <span className="text-sm">months</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Subscription Plans</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Basic Plan</span>
                  <div className="flex items-center gap-2">
                    <input type="number" value="15" className="w-16 px-2 py-1 border rounded text-sm" />
                    <span className="text-sm">KWD</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Professional</span>
                  <div className="flex items-center gap-2">
                    <input type="number" value="25" className="w-16 px-2 py-1 border rounded text-sm" />
                    <span className="text-sm">KWD</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Enterprise</span>
                  <div className="flex items-center gap-2">
                    <input type="number" value="50" className="w-16 px-2 py-1 border rounded text-sm" />
                    <span className="text-sm">KWD</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Platform Settings</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">Default Language</label>
                  <select className="w-full mt-1 px-3 py-2 border rounded-lg text-sm">
                    <option>Arabic</option>
                    <option>English</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Currency</label>
                  <select className="w-full mt-1 px-3 py-2 border rounded-lg text-sm">
                    <option>KWD - Kuwaiti Dinar</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 sm:mt-6 flex justify-end">
            <button className="bg-[#4B5563] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#374151] text-xs sm:text-sm w-full sm:w-auto">
              Save Configuration
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 sm:mt-8">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <CreditCardIcon className="w-4 sm:w-5 h-4 sm:h-5 text-[#008B8B] mr-2" />
            <span className="truncate">{t('quickActions')}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <button 
              onClick={() => navigate('/dashboard/garages')}
              className="p-4 sm:p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#008B8B] hover:scale-105"
            >
              <BuildingStorefrontIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mb-2 sm:mb-3" />
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Add New Garage</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Onboard a new garage to the platform</p>
            </button>
            <button 
              onClick={() => navigate('/dashboard/subscriptions')}
              className="p-4 sm:p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#FF8C00] hover:scale-105"
            >
              <CreditCardIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#FF8C00] mb-2 sm:mb-3" />
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">{t('subscriptions')}</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">View and manage all subscriptions</p>
            </button>
            <button 
              onClick={() => navigate('/dashboard/reports')}
              className="p-4 sm:p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#F59E0B] hover:scale-105"
            >
              <ChartBarIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#F59E0B] mb-2 sm:mb-3" />
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">{t('viewReports')}</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Generate platform analytics reports</p>
            </button>
            <button 
              onClick={() => navigate('/dashboard/admin-privileges')}
              className="p-4 sm:p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#8B5CF6] hover:scale-105"
            >
              <CogIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#8B5CF6] mb-2 sm:mb-3" />
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Admin Privileges</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage admin users and permissions</p>
            </button>
          </div>
        </div>
      </main>

      {/* Garage Details Modal */}
      {showGarageModal && selectedGarage && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold truncate pr-4">{selectedGarage.name}</h3>
                <button
                  onClick={() => setShowGarageModal(false)}
                  className="text-gray-400 hover:text-gray-500 flex-shrink-0"
                >
                  <XCircleIcon className="w-5 sm:w-6 h-5 sm:h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Monthly Revenue</p>
                  <p className="text-xl sm:text-2xl font-bold">{formatCurrency(selectedGarage.monthlyRevenue)}</p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Total Customers</p>
                    <p className="text-base sm:text-lg font-semibold">{selectedGarage.customerCount}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Subscription Status</p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedGarage.subscriptionStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {selectedGarage.subscriptionStatus}
                    </span>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                    <button className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 text-sm">
                      View Full Details
                    </button>
                    <button className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">
                      Contact Garage
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;